<template>
    <div class="create-edit-role">
        <Loader :loading="showLoader" />
        <v-card rounded="xl" elevation="4" class="mx-4 mt-4 mb-4 px-4 py-3"
          style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);">
          <div class="d-flex align-center">
            <v-btn icon variant="text" color="white" size="small" @click="goBack">
              <v-icon size="20">mdi-arrow-left</v-icon>
            </v-btn>
            <div class="ml-3 text-h6 font-weight-semibold" style="color: #ffffff;">
              {{ $t('roles.role-detail') }}
            </div>
            <v-spacer />
            <v-btn size="small" class="pulse-animation mr-1" variant="tonal" @click="goBack" color="white">{{
                $t('roles.abort') }}</v-btn>
            <v-btn size="small" class="pulse-animation" variant="tonal" @click="updateRole" color="white">{{
                $t('roles.update') }}</v-btn>
            <img :src="logo" class="toolbar-icon-white ml-2" />
          </div>
        </v-card>

        <!-- Role Information Section -->
        <div class="user-section">
            <h2>{{ $t('roles.role-info') }}</h2>
            <v-row>
                <v-col cols="12">
                    <v-text-field label="Role Name" v-model="role.name" variant="outlined" />
                </v-col>
                <v-col cols="12">
                    <v-textarea label="Description" v-model="role.description" variant="outlined" />
                </v-col>
            </v-row>
        </div>

        <!-- Application Functions Section -->
        <div class="location-section">
            <h2>{{ $t('roles.app-functions') }}</h2>
            <v-card-text class="tile-description">
                {{ $t('roles.permissions-description') }}
            </v-card-text>
            <v-container fluid>
                <v-row>
                    <v-col cols="12">
                        <v-switch label="Analytics" v-model="permissions.analytics" inset color="success"></v-switch>
                        <div class="divider"></div>
                    </v-col>
                    <v-col cols="12">
                        <v-switch label="Booking" v-model="permissions.booking" inset color="success"></v-switch>
                        <v-row>
                            <v-col cols="auto">
                                <v-checkbox label="Availability check and booking" v-model="permissions.bookingDetails"
                                    :disabled="!permissions.booking" dense></v-checkbox>
                            </v-col>
                        </v-row>
                        <div class="divider"></div>
                    </v-col>
                    <!-- <v-col cols="12">
                        <v-switch label="Displays" v-model="permissions.displays" inset color="success"></v-switch>
                        <div class="divider"></div>
                    </v-col> -->
                    <v-col cols="12">
                        <v-switch label="Operations" v-model="permissions.operations" inset color="success"></v-switch>
                        <v-row>
                            <v-col cols="auto">
                                <v-checkbox label="Booking overview"
                                    v-model="permissions.operationsDetails.bookingOverview"
                                    :disabled="!permissions.operations" dense></v-checkbox>
                            </v-col>
                            <v-col cols="auto">
                                <v-checkbox label="Community" v-model="permissions.operationsDetails.community"
                                    :disabled="!permissions.operations" dense></v-checkbox>
                            </v-col>
                            <v-col cols="auto">
                                <v-checkbox label="Guests" v-model="permissions.operationsDetails.guests"
                                    :disabled="!permissions.operations" dense></v-checkbox>
                            </v-col>
                            <v-col cols="auto">
                                <v-checkbox label="Locations" v-model="permissions.operationsDetails.locations"
                                    :disabled="!permissions.operations" dense></v-checkbox>
                            </v-col>
                        </v-row>
                        <div class="divider"></div>
                    </v-col>
                    <v-col cols="12">
                        <v-switch label="User Management" v-model="permissions.userManagement" inset
                            color="success"></v-switch>
                        <v-row>
                            <v-col cols="auto">
                                <v-checkbox label="User" v-model="permissions.userManagementDetails.user"
                                    :disabled="!permissions.userManagement" dense></v-checkbox>
                            </v-col>
                            <v-col cols="auto">
                                <v-checkbox label="Group" v-model="permissions.userManagementDetails.group"
                                    :disabled="!permissions.userManagement" dense></v-checkbox>
                            </v-col>
                            <v-col cols="auto">
                                <v-checkbox label="Role" v-model="permissions.userManagementDetails.role"
                                    :disabled="!permissions.userManagement" dense></v-checkbox>
                            </v-col>
                        </v-row>
                        <div class="divider"></div>
                    </v-col>
                </v-row>
            </v-container>
        </div>

        <!-- Activity Section -->
        <div class="activity-section">
            <h2> {{ $t('roles.assigned-users') }}</h2>
        </div>

    </div>
</template>

<script>
import { ref, defineComponent, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { getRoles } from '@/controllers/BackboneAPI';
import { getStoredData, getCampuses, isStaingOrQa } from '@/controllers/BaseController';
import Loader from '@/components/general/Loader.vue';
import lodash from 'lodash';
import { getToolbarLogoImage } from '@/assets/Branding/branding.js';

export default defineComponent({
    name: 'CreateEditRole',
    components: {
        Loader,
    },
    setup() {
        const showLoader = ref(true);
        const logo = ref('');
        const role = ref({
            name: '',
            description: '',
        });
        const permissions = ref({
            analytics: false,
            booking: false,
            bookingDetails: false,
            displays: false,
            operations: false,
            operationsDetails: {
                bookingOverview: false,
                community: false,
                guests: false,
                locations: false,
            },
            userManagement: false,
            userManagementDetails: {
                user: false,
                group: false,
                role: false,
            },
        });

        const route = useRoute();
        const isEdit = computed(() => !!route.params.id);
        const campuses = ref([]);

        const goBack = () => {
            window.history.back();
        };

        const updateRole = () => {
            console.log('Updating Role:', role.value, permissions.value);
            // Add your update logic here
        };

        onMounted(async () => {
            try {
                showLoader.value = true;
                logo.value = await getToolbarLogoImage();

                let CAMPUSES = lodash.chain(await getCampuses())
                    .filter({ active: true })
                    .orderBy('name', 'asc')
                    .value();
                if (await isStaingOrQa()) {
                    CAMPUSES = lodash.filter(CAMPUSES, (campus) => campus.name?.toLowerCase() !== 'hamburg');
                }
                campuses.value = CAMPUSES;

                if (isEdit.value) {
                    let USER = await getStoredData(route.params.id);
                    role.value.name = USER?.name || '';
                    role.value.description = USER?.description || '';
                }

            } catch (error) {
                console.error('Failed to load data:', error);
            } finally {
                showLoader.value = false;
            }
        });

        return {
            showLoader,
            logo,
            goBack,
            updateRole,
            role,
            permissions,
        };
    },
});
</script>

<style scoped>
.create-edit-role {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.toolbar-icon-white {
    height: 30px;
    filter: brightness(0) invert(1);
}

.user-section,
.location-section,
.activity-section {
    padding: 1rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #fff;
}

.divider {
    width: 100%;
    height: 1px;
    background-color: #e0e0e0;
    margin: 1rem 0;
}

.tile-description {
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    color: #666;
}

@media screen and (max-width: 768px) {
    h2 {
        font-size: 1.15rem;
    }

    .create-edit-user {
        padding: 0.5rem;
    }
}
</style>
